import React, { useCallback, useEffect, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';

import { PaymentCard } from '../../../PaymentCard';
import { Progress, stages } from '../../../Progress';
import { usePromiseWrapper } from '../../../../helpers/hooks';
import resources from '../../../../helpers/resources';
import { invoiceStatuses } from '../../../../helpers/constants';
import { ErrorBlock } from '../../../Block';

import SuccessIcon from '../../../../imgs/response-complete-icon.svg';
import RejectIcon from '../../../../imgs/response-reject-icon.svg';
import { DelayedRedirect } from './common';

const INTERVAL_BETWEEN_CHECKS = 15;

const resource = resources();

function FinalStatus({ icon, text, url }) {
  const { t } = useTranslation();

  return (
    <section className="block endpoint">
      <img src={icon} alt="" />
      <h3>{text}</h3>

      {url != null ? (
        <>
          <button
            className="btn"
            onClick={() => {
              window.location.assign(url);
            }}
          >
            {t(`Invoice.Result.ToShop`)}
          </button>
          <DelayedRedirect url={url} />
        </>
      ) : null}
    </section>
  );
}

function QrSection({ value, invoiceId, successUrl, failUrl }) {
  const [statusCheckCountdown, setStatusCheckCountdown] = useState(
    INTERVAL_BETWEEN_CHECKS
  );

  function resetCountdown() {
    setStatusCheckCountdown(INTERVAL_BETWEEN_CHECKS);
  }

  const checkStatus = useCallback(() => resource.getInvoiceStatus(invoiceId), [
    invoiceId,
  ]);

  const [statusRequest, pending] = usePromiseWrapper({
    initPromise: checkStatus,
    initialState: { loading: false },
  });

  const [invoiceStatus, setInvoiceStatus] = useState(invoiceStatuses.inProcess);

  useEffect(() => {
    if (invoiceStatus === invoiceStatuses.inProcess) {
      const interval = setInterval(() => {
        setStatusCheckCountdown((current) => (current > 0 ? current - 1 : 0));
      }, 1e3);

      return () => void clearInterval(interval);
    }
  }, [invoiceStatus]);

  useEffect(() => {
    if (statusCheckCountdown === 0) {
      async function check() {
        const resp = await statusRequest();

        if (resp != null) {
          setInvoiceStatus(resp.data);
        }

        resetCountdown();
      }

      check();
    }
  }, [statusCheckCountdown]);

  const { t } = useTranslation();

  const templateT = (key, params) =>
    t(`Invoice.Result.Templates.WeChat.${key}`, params);

  let block;
  switch (invoiceStatus) {
    case invoiceStatuses.inProcess: {
      block = (
        <section className="block wechat">
          <p>{templateT('Desc')}</p>

          <QRCodeSVG value={value} size={175} />

          <p className={classnames('status', { pending })}>
            <span>{templateT('Waiting')}</span>
            <span>{templateT('Checking')}</span>
          </p>

          <p className="counter">
            {
              <>
                {templateT('CountdownText')}
                <strong>
                  {templateT('CountdownTimer', {
                    countdown: statusCheckCountdown,
                  })}
                </strong>
              </>
            }
          </p>
        </section>
      );
      break;
    }
    case invoiceStatuses.success: {
      block = (
        <FinalStatus
          icon={SuccessIcon}
          text={templateT('Success')}
          url={successUrl}
        />
      );
      break;
    }
    case invoiceStatuses.fail: {
      block = (
        <FinalStatus
          icon={RejectIcon}
          text={templateT('Reject')}
          url={failUrl}
        />
      );
      break;
    }
    default: {
      block = <ErrorBlock headerText={t('DefaultError')} />;
    }
  }

  return (
    <>
      <Progress
        stage={
          invoiceStatus === invoiceStatuses.success
            ? stages.invoiceResult
            : stages.payment
        }
      />

      {block}
    </>
  );
}

export function WeChatResult({ invoice }) {
  const resultData = invoice.result.data;

  return (
    <React.Fragment>
      <PaymentCard
        shopOrderId={resultData.invoice_id}
        amount={resultData.amount}
        currency={resultData.currency}
        description={resultData.description}
        inlineInvoiceId={true}
      />

      <QrSection
        value={resultData.qr_code}
        invoiceId={resultData.encrypted_invoice_id}
        successUrl={resultData.success_url}
        failUrl={resultData.failed_url}
      />
    </React.Fragment>
  );
}
